import axios from 'axios';
import { Address } from 'viem';

export const COPY_BOT_API_URL = import.meta.env.VITE_COPY_BOT_API_URL || 'http://localhost:3000';

export const copyBotApi = axios.create({
  baseURL: COPY_BOT_API_URL,
});

export type CopyStrategy = 'PERCENTAGE' | 'FIXED' | 'ADAPTIVE';

export type LeaderDto = {
  address: Address;
  enabled?: boolean;
};

export type MultiplierTierDto = {
  min: number;
  max?: number | null;
  multiplier: number;
};

export type CopyStrategyConfigDto = {
  strategy: CopyStrategy;
  copySize: number;
  adaptiveMinPercent?: number;
  adaptiveMaxPercent?: number;
  adaptiveThreshold?: number;
  tieredMultipliers?: MultiplierTierDto[];
  tradeMultiplier?: number;
  minLeaderTradeSizeUSD?: number;
  maxOrderSizeUSD: number;
  minOrderSizeUSD: number;
  maxPositionSizeUSD?: number;
  maxDailyVolumeUSD?: number;
};

export type WalletSummaryDto = {
  deposit: number;
  totalTradeValue: number;
  openPositionsValue: number;
  unrealizedPnl: number;
  realizedPnl: number;
  pnl: number;
  roi: number;
};

export type WalletSetupStatusDto = {
  proxyDeployed: boolean;
  approvalsInitialized: boolean;
  clobCredentialsVerified?: boolean;
  runtimeAttached?: boolean;
  runtimeQueued?: boolean;
};

export type WalletResponse = {
  id: string;
  owner: Address;
  signerAddress: Address;
  proxyWallet: Address;
  enabled: boolean;
  leaders: LeaderDto[];
  copyStrategyConfig: CopyStrategyConfigDto;
  summary: WalletSummaryDto;
  setup?: WalletSetupStatusDto;
  hasPrivateKey: boolean;
  createdAt: string;
  updatedAt: string;
};

export type LeaderProfileDto = {
  address: Address;
  name?: string;
  pseudonym?: string;
  profileImage?: string;
  profileImageOptimized?: string;
  enabled?: boolean;
};

export type LeaderPositionDto = {
  leader: LeaderProfileDto;
  asset: string;
  conditionId: string;
  title: string;
  slug: string;
  icon?: string;
  eventSlug?: string;
  outcome: string;
  size: number;
  avgPrice: number;
  currentPrice: number;
  initialValue: number;
  currentValue: number;
  unrealizedPnl: number;
  realizedPnl: number;
  totalPnl: number;
  roi: number;
  percentPnl: number;
  percentRealizedPnl: number;
  isOpen: boolean;
  closedAt?: string;
  closeReason?: string;
  closedBySettlement?: boolean;
};

export type LeaderPositionsGroupDto = {
  leader: LeaderProfileDto;
  positionsCount: number;
  totalCurrentValue: number;
  totalInitialValue: number;
  unrealizedPnl: number;
  realizedPnl: number;
  totalPnl: number;
  roi: number;
};

export type LeaderOverviewResponse = {
  owner: Address;
  walletAddress: Address;
  leaders: LeaderPositionsGroupDto[];
};

export type PositionsResponse = {
  owner: Address;
  walletAddress: Address;
  positions: LeaderPositionDto[];
};

export type ActivityResponse = {
  owner: Address;
  walletAddress: Address;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  positions: LeaderPositionDto[];
};

export type CreateWalletRequest = {
  owner: Address;
  enabled?: boolean;
  leaders?: LeaderDto[];
};

export type AddLeaderRequest = {
  address: Address;
  enabled?: boolean;
};

export type BatchAddLeadersRequest = {
  leaders: LeaderDto[];
};

export type BatchRemoveLeadersRequest = {
  addresses: Address[];
};

export type ClosePositionRequest = {
  leaderAddress: Address;
  conditionId: string;
  asset: string;
};

export type ClosePositionResponse = {
  owner: Address;
  proxyWallet: Address;
  leaderAddress: Address;
  conditionId: string;
  asset: string;
  soldTokens: number;
  closeValue: number;
  closed: boolean;
  rebuyBlocked: boolean;
};

export type WithdrawUsdcRequest = {
  amountUsd: number;
  tokenAddress?: Address;
};

export type WithdrawUsdcResponse = {
  owner: Address;
  proxyWallet: Address;
  recipient: Address;
  amountUsd: number;
  tokenAddress?: Address;
  transactionHash?: string;
};

export type CreateChallengeRequest = {
  owner: Address;
};

export type CreateChallengeResponse = {
  owner: Address;
  nonce: string;
  message: string;
  expiresAt: string;
};

export type VerifySignatureRequest = {
  owner: Address;
  nonce: string;
  signature: Address | string;
};

export type VerifySignatureResponse = {
  owner: Address;
  token: string;
  expiresAt: string;
};

const authHeaders = (token: string) => ({
  Authorization: `Bearer ${token}`,
});

export const getWallet = async (owner: Address) => {
  const { data } = await copyBotApi.get<WalletResponse>(`/wallets/owner/${owner}`);
  return data;
};

export const getLeaderOverview = async (owner: Address) => {
  const { data } = await copyBotApi.get<LeaderOverviewResponse>(
    `/wallets/owner/${owner}/leaders/overview`
  );
  return data;
};

export const getPositions = async (owner: Address) => {
  const { data } = await copyBotApi.get<PositionsResponse>(`/wallets/owner/${owner}/positions`);
  return data;
};

export const getActivity = async (owner: Address, page = 1, limit = 20) => {
  const { data } = await copyBotApi.get<ActivityResponse>(`/wallets/owner/${owner}/activity`, {
    params: { page, limit },
  });
  return data;
};

export const createWallet = async (payload: CreateWalletRequest, token: string) => {
  const { data } = await copyBotApi.post<WalletResponse>('/wallets', payload, {
    headers: authHeaders(token),
  });
  return data;
};

export const addLeader = async (owner: Address, payload: AddLeaderRequest, token: string) => {
  const { data } = await copyBotApi.post<WalletResponse>(
    `/wallets/owner/${owner}/leaders`,
    payload,
    {
      headers: authHeaders(token),
    }
  );
  return data;
};

export const addLeaders = async (
  owner: Address,
  payload: BatchAddLeadersRequest,
  token: string
) => {
  const { data } = await copyBotApi.post<WalletResponse>(
    `/wallets/owner/${owner}/leaders/batch`,
    payload,
    { headers: authHeaders(token) }
  );
  return data;
};

export const removeLeader = async (owner: Address, leaderAddress: Address, token: string) => {
  const { data } = await copyBotApi.delete<WalletResponse>(
    `/wallets/owner/${owner}/leaders/${leaderAddress}`,
    { headers: authHeaders(token) }
  );
  return data;
};

export const removeLeaders = async (
  owner: Address,
  payload: BatchRemoveLeadersRequest,
  token: string
) => {
  const { data } = await copyBotApi.delete<WalletResponse>(
    `/wallets/owner/${owner}/leaders/batch`,
    {
      data: payload,
      headers: authHeaders(token),
    }
  );
  return data;
};

// =====================================================================
// Safe unfollow flow: list a leader's open positions and close them
// before unfollowing, so the user never gets orphaned positions in
// wallet_positions when they stop following a leader.
//
// Backend endpoints:
//   GET  /wallets/owner/:owner/leaders/:leaderAddress/open-positions
//   POST /wallets/owner/:owner/leaders/:leaderAddress/close-and-unfollow
//
// `closeAndUnfollow` is idempotent: if 0 positions are open it removes
// the leader immediately and returns totalAttempted=0, unfollowed=true.
// If any close fails, the leader is NOT removed and the per-position
// `attempts` array reports which ones failed and why, so the UI can
// surface a meaningful error and the user can retry.
// =====================================================================

export type LeaderOpenPosition = {
  conditionId: string;
  asset: string;
  title: string;
  slug: string;
  outcome: string;
  size: number;
  initialValue: number;
  currentValue: number;
  curPrice: number;
};

export type LeaderOpenPositionsResponse = {
  owner: Address;
  leaderAddress: Address;
  totalCount: number;
  positions: LeaderOpenPosition[];
};

export type CloseAndUnfollowAttempt = {
  conditionId: string;
  asset: string;
  title: string;
  closed: boolean;
  soldTokens: number;
  closeValue: number;
  error?: string;
};

export type CloseAndUnfollowResponse = {
  owner: Address;
  leaderAddress: Address;
  unfollowed: boolean;
  totalAttempted: number;
  totalClosed: number;
  totalFailed: number;
  attempts: CloseAndUnfollowAttempt[];
};

export const getLeaderOpenPositions = async (owner: Address, leaderAddress: Address) => {
  const { data } = await copyBotApi.get<LeaderOpenPositionsResponse>(
    `/wallets/owner/${owner}/leaders/${leaderAddress}/open-positions`
  );
  return data;
};

export const closeAndUnfollow = async (
  owner: Address,
  leaderAddress: Address,
  token: string
) => {
  const { data } = await copyBotApi.post<CloseAndUnfollowResponse>(
    `/wallets/owner/${owner}/leaders/${leaderAddress}/close-and-unfollow`,
    {},
    { headers: authHeaders(token) }
  );
  return data;
};

export const updateStrategy = async (
  owner: Address,
  payload: CopyStrategyConfigDto,
  token: string
) => {
  const { data } = await copyBotApi.patch<WalletResponse>(
    `/wallets/owner/${owner}/strategy`,
    payload,
    { headers: authHeaders(token) }
  );
  return data;
};

export const closePosition = async (
  owner: Address,
  payload: ClosePositionRequest,
  token: string
) => {
  const { data } = await copyBotApi.post<ClosePositionResponse>(
    `/wallets/owner/${owner}/positions/close`,
    payload,
    { headers: authHeaders(token) }
  );
  return data;
};

export const withdrawUsdc = async (owner: Address, payload: WithdrawUsdcRequest, token: string) => {
  const { data } = await copyBotApi.post<WithdrawUsdcResponse>(
    `/wallets/owner/${owner}/withdraw-usdc`,
    payload,
    { headers: authHeaders(token) }
  );
  return data;
};

export const createChallenge = async (payload: CreateChallengeRequest) => {
  const { data } = await copyBotApi.post<CreateChallengeResponse>('/auth/challenge', payload);
  return data;
};

export const verifySignature = async (payload: VerifySignatureRequest) => {
  const { data } = await copyBotApi.post<VerifySignatureResponse>('/auth/verify', payload);
  return data;
};
